import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { CheckCircle2, Lock, Sparkles } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import { usePalette } from "@/components/theme/PaletteProvider";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import { isStudioEarlyAccessFeature } from "@/lib/authz/policy";
import { planOfferForFeature } from "@/lib/authz/featurePlans";
import { hasFeature, lockedToolHref } from "@/lib/billing/planLabel";
import { useToolAccess } from "@/lib/home/planPreview";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";
import { trackClientEvent } from "@/lib/tracking/client";
import { cn } from "@/lib/utils";
import { NEW_FEATURE_TOOLS, type NewFeatureTool } from "./NewFeatureRow";

// "Don't show again" is remembered per tool, so the next tool added to the top
// of NEW_FEATURE_TOOLS pops up again for everyone. Closing without it only
// hides the popup for this browser session; the next visit shows it again.
const NEVER_KEY_PREFIX = "workshop_new_tool_popup_never:";
const SESSION_KEY_PREFIX = "workshop_new_tool_popup_closed:";

// Promo clips, keyed by NEW_FEATURE_TOOLS key. The popup is generic: it pops
// for the newest NEW tool that is locked for the viewer AND has an entry here
// (tools without a clip never pop). To promote a tool, add one entry; the clip
// plays instead of the static preview image. Studio early-access tools get
// Studio copy automatically via isStudioEarlyAccessFeature.
export const PROMO_VIDEOS: Partial<Record<string, { src: string; poster: string }>> = {};

function readFlag(storage: () => Storage, key: string) {
  try {
    return storage().getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(storage: () => Storage, key: string) {
  try { storage().setItem(key, "1"); } catch { /* storage blocked */ }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;
const noopSubscribe = () => () => {};

/**
 * "Here's the latest tool" popup on the subscriber home. Shows the newest
 * NEW_FEATURE_TOOLS entry that has a promo video each time a signed-in user whose plan lacks it
 * enters the Workshop, until they tick "Don't show again". Members who
 * already have the tool never see it.
 */
export function NewToolPopup() {
  const { loading, user } = useAuth();
  // Preview-aware, so an admin using "Preview as plan" sees what that plan sees.
  const { permissions, isAdmin } = useToolAccess();
  const tool = NEW_FEATURE_TOOLS.find(
    (t) => PROMO_VIDEOS[t.key] && !hasFeature(permissions, t.featureKey, { isAdmin }),
  );
  const [open, setOpen] = useState(false);
  const locked = Boolean(tool);

  useEffect(() => {
    if (loading || !user || !tool) return;
    if (!locked) {
      setOpen(false);
      return;
    }
    if (readFlag(local, NEVER_KEY_PREFIX + tool.key)) return;
    if (readFlag(session, SESSION_KEY_PREFIX + tool.key)) return;
    setOpen(true);
  }, [loading, user, tool, locked]);

  if (!tool) return null;

  const close = (never: boolean) => {
    writeFlag(session, SESSION_KEY_PREFIX + tool.key);
    if (never) writeFlag(local, NEVER_KEY_PREFIX + tool.key);
    setOpen(false);
  };

  return <NewToolPopupDialog tool={tool} open={open} onClose={close} />;
}

/** The popup itself, without the who-sees-it logic. Always pitches the upgrade. */
export function NewToolPopupDialog({
  tool,
  open,
  onClose,
}: {
  tool: NewFeatureTool;
  open: boolean;
  onClose: (never: boolean) => void;
}) {
  const { data: serviceSettings = [] } = useServiceSettings();
  const { palette: selectedPalette } = usePalette();
  const hydrated = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const [never, setNever] = useState(false);

  const plan = planOfferForFeature(tool.featureKey);
  const video = PROMO_VIDEOS[tool.key];
  const image = video
    ? null
    : resolveToolPreviewImage({
      palette: hydrated ? selectedPalette : "classic",
      href: tool.href,
      featureKey: tool.featureKey,
      serviceSettings,
    });
  const Icon = tool.icon;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(never); }}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-xl gap-0 overflow-y-auto rounded-2xl p-0 sm:rounded-2xl">
        <div className={cn("aspect-video max-h-[40dvh] w-full overflow-hidden bg-gradient-to-br", tool.gradient)}>
          {video ? (
            <video
              className="h-full w-full bg-black object-cover"
              src={video.src}
              poster={video.poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : image ? (
            <img src={image} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Icon className="h-14 w-14 text-foreground/70" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="space-y-4 p-5 sm:p-6">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary">
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              Latest tool
            </span>
            <DialogTitle className="text-xl font-semibold tracking-tight">
              Here&apos;s the latest tool: {tool.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {tool.blurb}
            </DialogDescription>
          </div>
          <ul className="space-y-2 text-sm">
            {tool.features.map((feature) => (
              <li key={feature} className="flex gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={never} onCheckedChange={(v) => setNever(v === true)} />
              Don&apos;t show again
            </label>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                className="rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => onClose(never)}
              >
                Maybe later
              </button>
              <Link
                href={lockedToolHref(tool.featureKey)}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                onClick={() => {
                  trackClientEvent("upgrade_clicked", { tool: tool.featureKey, to_plan: plan.tier });
                  onClose(never);
                }}
              >
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                {isStudioEarlyAccessFeature(tool.featureKey) ? "Upgrade to Studio" : `Get early access with ${plan.name}`}
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
